import {
  Alert,
  AlertTitle,
  Box,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import { memo, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { type TenediFieldChangeFieldsFragment } from "../../../generated/graphql";
import {
  crownImportCustomerLabels,
  crownImportExportIdentity,
  crownImportSummary,
} from "../crown-import";
import {
  changeFieldLabel,
  changeValueLabel,
  CROWN_IMPORT_ACTION_LABELS,
  CROWN_IMPORT_COPY,
  CROWN_IMPORT_OUTCOME_LABELS,
} from "../tenedi-connection-copy";
import { tenediConnectionLinkFor } from "../tenedi-connection-links";
import { type CrownImportReport } from "../use-crown-import";

const styles = {
  list: { m: 0, pl: 2 } satisfies SxProps<Theme>,
  notes: { m: 0, pl: 2, typography: "body2" } satisfies SxProps<Theme>,
  identity: { mb: 1 } satisfies SxProps<Theme>,
} as const;

type Partner = CrownImportReport["partners"][number];

const changeLine = (
  change: TenediFieldChangeFieldsFragment,
  scope: "carrier" | "customer" | "partnership",
): string =>
  `${changeFieldLabel(change.field, scope)}: ${changeValueLabel(
    change.before,
  )} to ${changeValueLabel(change.after)}`;

/** The partner's problem, warnings and changes, as short lines for its row. */
export const partnerNotes = (partner: Partner): string[] => [
  ...partner.identityChanges.map((change) => changeLine(change, "customer")),
  ...partner.partnerships.flatMap((partnership) =>
    partnership.changes.map(
      (change) => `${partnership.scac} ${changeLine(change, "partnership")}`,
    ),
  ),
  ...partner.unmatchedServiceNames.map(
    (name) => `No TMS service named ${name}; its codes are not mapped.`,
  ),
  ...(isNil(partner.serviceMappingsError)
    ? []
    : [`Service codes not saved: ${partner.serviceMappingsError}`]),
  ...partner.warnings,
];

type PartnerRowProps = {
  readonly carrierUuid: string;
  readonly partner: Partner;
};

const PartnerRow = memo(({ carrierUuid, partner }: PartnerRowProps) => {
  const notes = useMemo(() => partnerNotes(partner), [partner]);
  const customer = useMemo(() => crownImportCustomerLabels(partner), [partner]);
  return (
    <TableRow>
      <TableCell>{partner.partnerIsaId}</TableCell>
      <TableCell>
        {isNil(partner.organizationUuid) ? (
          customer.name
        ) : (
          <Link
            component={RouterLink}
            to={tenediConnectionLinkFor({
              companyUuid: carrierUuid,
              organizationUuid: partner.organizationUuid,
            })}
          >
            {customer.name}
          </Link>
        )}
        <Typography color="text.secondary" variant="body2">
          {customer.source}
          {isNil(partner.customerOutcome)
            ? ""
            : ` · ${CROWN_IMPORT_OUTCOME_LABELS[partner.customerOutcome]}`}
        </Typography>
      </TableCell>
      <TableCell>
        {partner.partnerships
          .map(
            (partnership) =>
              `${partnership.scac} ${CROWN_IMPORT_ACTION_LABELS[partnership.action]}`,
          )
          .join(", ")}
      </TableCell>
      <TableCell>
        {!isNil(partner.error) && (
          <Typography color="error" variant="body2">
            {partner.error}
          </Typography>
        )}
        {notes.length > 0 && (
          <Box component="ul" sx={styles.notes}>
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
});
PartnerRow.displayName = "PartnerRow";

type CrownImportReportViewProps = {
  readonly report: CrownImportReport;
};

/** What a Crown import planned or did: the carrier, each partner, and what it left alone. */
const CrownImportReportView = memo(({ report }: CrownImportReportViewProps) => (
  <Stack spacing={2}>
    <Alert severity={report.applied ? "success" : "info"}>
      <AlertTitle>
        {report.crownId} ({report.exportCompanyName}):{" "}
        {crownImportSummary(report)}
      </AlertTitle>
      <Typography sx={styles.identity} variant="body2">
        {crownImportExportIdentity(report)}
      </Typography>
      {report.applied ? CROWN_IMPORT_COPY.applied : CROWN_IMPORT_COPY.planned}
      {report.carrier.changes.length > 0 && (
        <Box component="ul" sx={styles.list}>
          {report.carrier.changes.map((change) => (
            <li key={change.field}>
              {report.carrier.name} {changeLine(change, "carrier")}
            </li>
          ))}
        </Box>
      )}
    </Alert>
    {report.partners.length > 0 && (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Partner EDI ID</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Partnerships</TableCell>
            <TableCell>Changes and notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.partners.map((partner) => (
            <PartnerRow
              key={`${partner.partnerIsaId}-${partner.organizationUuid ?? partner.accountId}`}
              carrierUuid={report.carrier.uuid}
              partner={partner}
            />
          ))}
        </TableBody>
      </Table>
    )}
    {report.notImportedPartners.length > 0 && (
      <Alert severity="warning">
        <AlertTitle>{CROWN_IMPORT_COPY.notImportedTitle}</AlertTitle>
        <Box component="ul" sx={styles.list}>
          {report.notImportedPartners.map((partner) => (
            <li key={partner.partnerIsaId}>
              {partner.partnerIsaId} ({partner.customerName},{" "}
              {partner.accountId}
              ): {partner.reason}
            </li>
          ))}
        </Box>
      </Alert>
    )}
    {report.warnings.length > 0 && (
      <Alert severity="warning">
        <AlertTitle>{CROWN_IMPORT_COPY.warningsTitle}</AlertTitle>
        <Box component="ul" sx={styles.list}>
          {report.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </Box>
      </Alert>
    )}
    {report.skippedLinks.length > 0 && (
      <Alert severity="info">
        <AlertTitle>
          {CROWN_IMPORT_COPY.skippedLinksTitle} ({report.skippedLinks.length})
        </AlertTitle>
        <Box component="ul" sx={styles.list}>
          {report.skippedLinks.map((link) => (
            <li key={link.linkKey}>
              {link.linkKey}: {link.reason}
            </li>
          ))}
        </Box>
      </Alert>
    )}
  </Stack>
));
CrownImportReportView.displayName = "CrownImportReportView";

export default CrownImportReportView;
